import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface HintPlace {
  left: number
  top: number
  side: 'beside' | 'below'
}

interface Edges {
  left: number
  right: number
  top: number
  bottom: number
}

const GAP = 12
const MARGIN = 8

/**
 * Where a menu entry's line of detail goes: just past the end of its label, on the
 * same line, when the card fits there; otherwise under the label. Never outside the
 * window. A row spans the whole menu, so anchoring to the row rather than to its
 * words put the card a menu's width from the words it explains.
 */
export function placeHint(label: Edges, width: number, viewport: number): HintPlace {
  if (label.right + GAP + width <= viewport - MARGIN) {
    return { left: label.right + GAP, top: (label.top + label.bottom) / 2, side: 'beside' }
  }
  return {
    left: Math.max(MARGIN, Math.min(label.left, viewport - MARGIN - width)),
    top: label.bottom + GAP / 2,
    side: 'below',
  }
}

interface Shown {
  text: string
  label: Edges
}

/**
 * One card for every menu entry, held at the window rather than inside the menu, so
 * it can be measured and kept on screen. `bind(text)` goes on an entry; `hide()` is
 * for when the menu closes under the pointer, which fires no mouseleave.
 */
export function useMenuHint(): {
  bind: (text: string) => {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void
    onFocus: (e: React.FocusEvent<HTMLElement>) => void
    onMouseLeave: () => void
    onBlur: () => void
  }
  hide: () => void
  card: ReactNode
} {
  const [shown, setShown] = useState<Shown | null>(null)
  const [place, setPlace] = useState<HintPlace | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  const show = (entry: HTMLElement, text: string) => {
    if (!text) return
    const words = entry.querySelector('.menu-item-label') ?? entry
    const { left, right, top, bottom } = words.getBoundingClientRect()
    setPlace(null)
    setShown({ text, label: { left, right, top, bottom } })
  }
  const hide = () => setShown(null)

  // Measured before it is painted: rendered unseen at the corner, then placed.
  useLayoutEffect(() => {
    if (!shown || !ref.current) return
    setPlace(placeHint(shown.label, ref.current.offsetWidth, document.documentElement.clientWidth))
  }, [shown])

  // A card left where the words used to be is worse than none.
  useEffect(() => {
    if (!shown) return
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    return () => {
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
    }
  }, [shown])

  const card = shown
    ? createPortal(
        <div
          ref={ref}
          className={`menu-hint${place ? ` ${place.side}` : ''}`}
          data-testid="menu-hint"
          aria-hidden
          style={place ? { left: place.left, top: place.top } : { left: 0, top: 0, visibility: 'hidden' }}
        >
          {shown.text}
        </div>,
        document.body,
      )
    : null

  return {
    bind: (text) => ({
      onMouseEnter: (e) => show(e.currentTarget, text),
      onFocus: (e) => {
        if (e.currentTarget.matches(':focus-visible')) show(e.currentTarget, text)
      },
      onMouseLeave: hide,
      onBlur: hide,
    }),
    hide,
    card,
  }
}
